"use client"

import { useQuery } from "@tanstack/react-query"
import { authService } from "@/services/auth"

export interface User {
    id: number
    email: string
    full_name?: string
    avatar_url?: string
    is_active: boolean
    is_superuser: boolean
    role?: number
    role_name?: string
    bio?: string
    particle_balance?: number
    referral_code?: string
    referred_by_id?: number
    field_of_study?: string
    institution?: string
    is_profile_complete_reward_claimed?: boolean
    active_plan?: string
    membership_expiry?: string
    has_learn_access?: boolean
    profile_slug?: string
}

export function useAuth() {
    // 1. Check lightweight auth status first
    const {
        data: authStatus,
        isLoading: isCheckingStatus
    } = useQuery({
        queryKey: ["authStatus"],
        queryFn: authService.checkStatus,
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
    })

    const isAuthenticated = authStatus?.is_authenticated ?? false;

    // 2. Fetch full user profile ONLY IF authenticated
    const {
        data: user,
        isLoading: isFetchingUser,
        error
    } = useQuery<User>({
        queryKey: ["currentUser"],
        queryFn: authService.getCurrentUser,
        enabled: isAuthenticated, // <-- Prevents 401 redirect loop
        retry: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
    })

    return {
        user,
        isLoading: isCheckingStatus || (isAuthenticated && isFetchingUser),
        isAuthenticated,
        error
    }
}
